import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wrench, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { API_BASE } from '../../config/api';

const DEFAULT_MESSAGE = "We're making some improvements. Please check back shortly.";

export default function MaintenanceModeSettings({ token }) {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_BASE}/api/admin/system/maintenance`, { headers })
      .then((r) => { setData(r.data); setMessage(r.data.message || DEFAULT_MESSAGE); })
      .catch(() => toast.error('Could not load maintenance settings'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const save = async (enabled) => {
    if (enabled && !data?.enabled && !window.confirm('Turn maintenance mode ON? Everyone except admins will see the maintenance page.')) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API_BASE}/api/admin/system/maintenance`, { enabled, message }, { headers });
      setData(r.data);
      toast.success(enabled ? 'Maintenance mode is ON' : 'Maintenance mode is OFF');
      window.dispatchEvent(new Event('maintenance-changed'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-navigate-gold" /></div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="maintenance-settings">
      <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-2"><Wrench className="h-6 w-6 text-navigate-gold" /> Maintenance Mode</h2>
      <div className="p-5 bg-navigate-surface border border-zinc-800 rounded-xl flex items-center justify-between gap-4">
        <div>
          <p className="text-white font-semibold" data-testid="maintenance-status">{data.enabled ? 'ON - only admins can use the site' : 'OFF - site is open to signed-in users'}</p>
          <p className="text-sm text-zinc-400 mt-1">When on, learners and visitors see the maintenance page. Admins can still sign in and use everything.</p>
          {data.updated_by && <p className="text-xs text-zinc-500 mt-2">Last changed by {data.updated_by} on {new Date(data.updated_at).toLocaleString()}</p>}
        </div>
        <Switch checked={data.enabled} disabled={saving} onCheckedChange={save} data-testid="maintenance-toggle" />
      </div>
      <div className="space-y-2">
        <label htmlFor="maintenance-message" className="text-sm text-zinc-300">Message shown on the maintenance page</label>
        <Textarea id="maintenance-message" value={message} maxLength={500} onChange={(e) => setMessage(e.target.value)}
          className="bg-zinc-900 border-zinc-700 text-white" rows={3} data-testid="maintenance-message-input" />
        <Button onClick={() => save(data.enabled)} disabled={saving} className="btn-gold" data-testid="maintenance-save-message">Save message</Button>
      </div>
    </div>
  );
}
